import type {
  BannerSwap,
  BannerSwapQueueRequest,
  BannerSwapRequest,
  BannerSwapOverlapAnalysis,
  BannerSwapOverlapResolution,
} from "../utils/@types";
import apiClient from "./middleware";
import { normalizeBannerSwapQueueRequest } from "./normalizeBannerSwapQueueRequest";

export type BannerAuditLogEntry = {
  id: string;
  action: string;
  actorEmail: string;
  occurredAt: string;
  channelId?: string;
  channelIds?: string[];
  swapUID?: string;
  metadata?: Record<string, unknown>;
};

/** V2 Banner Dashboard API — same routes as legacy; enriched default-banner persistence. */
export const bannerDashboardApi = {
  getBannerSwapQueue: async (): Promise<BannerSwap[]> => {
    const response = await apiClient.get<{ data: BannerSwap[] }>(
      "/BannerSwap/Queue"
    );
    return response.data.data;
  },

  postBannerSwap: async (swap: BannerSwapQueueRequest) => {
    const response = await apiClient.post(
      `/BannerSwap/Queue`,
      normalizeBannerSwapQueueRequest(swap)
    );
    return response.data;
  },

  postChangeDefaultBanner: async (body: {
    channelIDs: string[];
    channelBannerURL: string;
    channelBannerImage: string;
  }) => {
    const response = await apiClient.post(`/BannerSwap/ChangeDefault`, body);
    return response.data as {
      message: string;
      appliedImmediately: string[];
      deferredUntilSwapEnd: string[];
      failed: Array<{ channelId: string; error: string }>;
    };
  },

  postBannerSwapOverlapCheck: async (body: {
    channelIDs: string[];
    startDate: Date | string;
    endDate: Date | string;
    excludeSwapUID?: string;
  }): Promise<BannerSwapOverlapAnalysis> => {
    const response = await apiClient.post(`/BannerSwap/OverlapCheck`, body);
    return response.data;
  },

  updateBannerSwapStatus: async (
    bannerSwapId: BannerSwap["swapUID"],
    status: BannerSwap["status"]
  ) => {
    const response = await apiClient.patch(`/BannerSwap/Status`, {
      bannerSwapId,
      status,
    });
    return response.data;
  },

  endBannerSwapNow: async (bannerSwapId: BannerSwap["swapUID"]) => {
    const response = await apiClient.patch(`/BannerSwap/EndNow`, {
      bannerSwapId,
    });
    return response.data;
  },

  patchBannerSwapRunningEndDate: async (
    bannerSwapId: BannerSwap["swapUID"],
    endDate: Date
  ) => {
    const response = await apiClient.patch(`/BannerSwap/RunningEndDate`, {
      bannerSwapId,
      endDate: endDate.toISOString(),
    });
    return response.data;
  },

  patchBannerSwapSchedule: async (
    bannerSwapId: BannerSwap["swapUID"],
    startDate: Date,
    endDate: Date,
    overlapResolution?: BannerSwapOverlapResolution
  ) => {
    const response = await apiClient.patch(`/BannerSwap/Schedule`, {
      bannerSwapId,
      startDate,
      endDate,
      ...(overlapResolution ? { overlapResolution } : {}),
    });
    return response.data;
  },

  patchRemoveChannelsFromSwap: async (
    bannerSwapId: BannerSwap["swapUID"],
    channelIdsToRemove: string[]
  ) => {
    const response = await apiClient.patch(`/BannerSwap/RemoveChannels`, {
      bannerSwapId,
      channelIdsToRemove,
    });
    return response.data as {
      message: string;
      removedChannelIds: string[];
      remainingChannelIds: string[];
      swapCancelled: boolean;
      endTasksEnqueued?: number;
    };
  },

  updateDefaultBanner: async (
    channelId: string,
    bannerURL: string,
    defaultBannerStoragePath?: string
  ) => {
    const response = await apiClient.put(`/channel-settings/default-banner`, {
      channelId,
      bannerURL,
      ...(defaultBannerStoragePath?.trim()
        ? { defaultBannerStoragePath: defaultBannerStoragePath.trim() }
        : {}),
    });
    return response.data;
  },

  getDefaultBanner: async (channelId: string) => {
    const response = await apiClient.get(`/channel-settings/default-banner`, {
      params: { channelId },
    });
    return response.data as {
      channelId: string;
      defaultBannerURL: string;
      defaultBannerStoragePath?: string | null;
    };
  },

  getBannerAuditLogs: async (params: {
    channelId?: string;
    swapUID?: string;
    limit?: number;
  }): Promise<BannerAuditLogEntry[]> => {
    const response = await apiClient.get<{ data: BannerAuditLogEntry[] }>(
      "/banner-audit-log",
      { params }
    );
    return response.data.data;
  },

  acceptCurrentBannerAsDefault: async (channelId: string) => {
    const response = await apiClient.post(
      `/channel-settings/accept-current-as-default`,
      { channelId }
    );
    return response.data as {
      message: string;
      data: {
        channelId: string;
        defaultBannerURL: string;
        defaultBannerStoragePath: string;
        bannerState?: string;
      };
    };
  },
};
